/**
 * personalized-pdp.js
 * Alpine.js store + components for the personalized product PDP.
 *
 * LOADING ORDER (critical):
 *   1. This file (registers alpine:init listener)
 *   2. alpine.min.js   (fires alpine:init → listener registers store & components)
 *
 * Both scripts are loaded with `defer` in the HTML; the HTML ordering guarantees
 * this file runs first and its alpine:init listener is in place before Alpine starts.
 */

/**
 * Apply a text transform to a string.
 * @param {string} text
 * @param {string} transform  'uppercase' | 'lowercase' | ''
 * @returns {string}
 */
function applyTextTransform(text, transform) {
  if (transform === 'uppercase') return text.toUpperCase();
  if (transform === 'lowercase') return text.toLowerCase();
  return text;
}

/**
 * Build a fluid font-size expression anchored to two render widths.
 * The returned value uses rem + cqw and is clamped between the two anchors.
 *
 * Anchors:
 * - mobile:  350px render width
 * - desktop: 665px render width
 *
 * @param {number|string} mobilePx
 * @param {number|string} desktopPx
 * @returns {string}
 */
function buildFluidFontSize(mobilePx, desktopPx) {
  const MOBILE_RENDER_WIDTH = 350;
  const DESKTOP_RENDER_WIDTH = 665;
  const MAX_RENDER_WIDTH = 1225;
  const ROOT_FONT_SIZE = 16;

  const parsedMobilePx = parseFloat(mobilePx);
  const parsedDesktopPx = parseFloat(desktopPx);

  const safeMobilePx = Number.isFinite(parsedMobilePx) ? parsedMobilePx : 16;
  const safeDesktopPx = Number.isFinite(parsedDesktopPx) ? parsedDesktopPx : safeMobilePx;

  const mobileRem = safeMobilePx / ROOT_FONT_SIZE;
  const desktopRem = safeDesktopPx / ROOT_FONT_SIZE;
  const maxRem = desktopRem * (MAX_RENDER_WIDTH / DESKTOP_RENDER_WIDTH);

  // For 1cqw, width contribution in rem is: renderWidthPx / 1600.
  const cqwSlope = (desktopRem - mobileRem) * (1600 / (DESKTOP_RENDER_WIDTH - MOBILE_RENDER_WIDTH));
  const interceptRem = mobileRem - (cqwSlope * MOBILE_RENDER_WIDTH) / 1600;

  const minRem = Math.min(mobileRem, desktopRem, maxRem);
  const cappedMaxRem = Math.max(mobileRem, desktopRem, maxRem);

  return `clamp(${minRem}rem, calc(${interceptRem}rem + ${cqwSlope}cqw), ${cappedMaxRem}rem)`;
}

function registerPersonalizationComponents() {
  if (!window.Alpine || window.__personalizationComponentsRegistered) return;
  window.__personalizationComponentsRegistered = true;

  // ============================================================
  // STORE — shared reactive state across all render instances
  // ============================================================

  Alpine.store('personalization', {
    color: { name: '', value: '' },
    font: { name: '' },
    inputType: 'single_line', // 'single_line' | 'two_lines'
    inputFieldName: 'Name',
    name: '',
    nameLines: { line1: '', line2: '' },
    textTransform: '', // 'uppercase' | 'lowercase' | ''
    returnAddress: { enabled: false, text: '' },

    // Variant → preview image URL map (populated from render snippet data attr)
    variantPreviewImages: {},
    currentPreviewImageUrl: '',

    setColor(name, value) {
      this.color = { name, value };
    },

    setFont(name) {
      this.font = { name };
    },

    setInputType(type) {
      this.inputType = type === 'two_lines' ? 'two_lines' : 'single_line';
    },

    setInputFieldName(name) {
      this.inputFieldName = (name || 'Name').trim() || 'Name';
    },

    setTextTransform(value) {
      const normalized = (value || '').trim().toLowerCase();
      this.textTransform = (normalized === 'uppercase' || normalized === 'lowercase') ? normalized : '';
    },

    setName(text) {
      this.name = text;
    },

    setNameLines(line1, line2) {
      this.nameLines = {
        line1: line1 || '',
        line2: line2 || ''
      };
    },

    setReturnAddressEnabled(enabled) {
      this.returnAddress = { ...this.returnAddress, enabled };
    },

    setReturnAddressText(text) {
      this.returnAddress = { ...this.returnAddress, text };
    },

    setVariantPreviewImages(map) {
      this.variantPreviewImages = map;
    },

    updatePreviewImageForVariant(variantId) {
      const url =
        this.variantPreviewImages[variantId] ??
        this.variantPreviewImages[String(variantId)];
      if (url) this.currentPreviewImageUrl = url;
    },

    /**
     * Returns line item properties object for the cart form submission.
     */
    getCartProperties() {
      const props = {
        'Lettering Color': this.color.name,
        'Font': this.font.name
      };

      if (this.inputType === 'two_lines') {
        props[`${this.inputFieldName} Line 1`] = this.nameLines.line1;
        props[`${this.inputFieldName} Line 2`] = this.nameLines.line2;
      } else {
        props['Name'] = this.name;
      }

      if (this.returnAddress.enabled && this.returnAddress.text) {
        props['Return Address'] = this.returnAddress.text;
      }

      return props;
    },

    getManagedPropertyKeys() {
      const keys = ['Lettering Color', 'Font', 'Name', 'Return Address'];

      if (this.inputType === 'two_lines') {
        keys.push(`${this.inputFieldName} Line 1`, `${this.inputFieldName} Line 2`);
      }

      return keys;
    },

    /**
     * Returns true only when the minimum required fields are filled in.
     * Note: color is always required even if the picker is hidden (auto-selected).
     */
    isValid() {
      return (
        this.color.name !== '' &&
        this.font.name !== '' &&
        this.name.trim() !== ''
      );
    },

    reset() {
      this.color = { name: '', value: '' };
      this.font = { name: '' };
      this.inputType = 'single_line';
      this.inputFieldName = 'Name';
      this.name = '';
      this.nameLines = { line1: '', line2: '' };
      this.returnAddress = { enabled: false, text: '' };
    }
  });

  // ============================================================
  // COMPONENT — personalizationOptions
  // Manages the personalization form controls (colors, fonts, name, return address).
  // ============================================================

  Alpine.data('personalizationOptions', () => ({
    selectedColor: { name: '', value: '' },
    selectedFont: { name: '' },
    selectedLiner: '',
    nameText: '',
    line1Text: '',
    line2Text: '',
    isTwoLineInput: false,
    showAddress: false,
    returnAddressText: '',
    fontTransforms: {},

    get line1Length() {
      return this.line1Text.length;
    },

    get line2Length() {
      return this.line2Text.length;
    },

    buildTwoLineStoredName(line1, line2) {
      if (line1 && line2) return `${line1}\n${line2}`;
      return line1 || line2 || '';
    },

    updateTwoLineName() {
      const transform = this.$store.personalization.textTransform;
      const storedLine1 = applyTextTransform(this.line1Text, transform);
      const storedLine2 = applyTextTransform(this.line2Text, transform);

      this.$store.personalization.setNameLines(storedLine1, storedLine2);
      this.$store.personalization.setName(
        this.buildTwoLineStoredName(storedLine1, storedLine2)
      );

      this.$dispatch('personalization-changed', this.$store.personalization);
    },

    init() {
      const inputTypeRaw = (this.$el.dataset.inputFieldType || '').trim().toLowerCase();
      this.isTwoLineInput = inputTypeRaw === 'two text lines';
      this.$store.personalization.setInputType(this.isTwoLineInput ? 'two_lines' : 'single_line');
      this.$store.personalization.setInputFieldName(this.$el.dataset.inputFieldName || 'Name');

      // --- Colour ---
      const checkedColorInput = this.$el.querySelector(
        'input[name="personalization-color"]:checked'
      );

      const defaultColorName = (this.$el.dataset.defaultColorName || '').trim();
      const defaultColorValue = (this.$el.dataset.defaultColorValue || '').trim();

      if (checkedColorInput) {
        this.selectedColor = {
          name: checkedColorInput.value,
          value: checkedColorInput.dataset.colorValue || ''
        };
        this.$store.personalization.setColor(
          checkedColorInput.value,
          checkedColorInput.dataset.colorValue || ''
        );
      } else if (defaultColorValue) {
        this.selectedColor = {
          name: defaultColorName,
          value: defaultColorValue
        };
        this.$store.personalization.setColor(defaultColorName, defaultColorValue);
      }

      // --- Font ---
      const checkedFontInput = this.$el.querySelector(
        'input[name="personalization-font"]:checked'
      );
      if (checkedFontInput) {
        this.selectedFont = { name: checkedFontInput.value };
        this.$store.personalization.setFont(checkedFontInput.value);
      }

      // --- Font transforms ---
      try {
        this.fontTransforms = JSON.parse(this.$el.dataset.fontTransforms || '{}');
      } catch (e) {
        this.fontTransforms = {};
      }

      // Set initial text transform from the initially-checked font
      if (checkedFontInput) {
        const initialTransform = this.fontTransforms[checkedFontInput.value] || '';
        this.$store.personalization.setTextTransform(initialTransform);
      }

      // --- Liner (custom UI mirrors hidden native variant option) ---
      const checkedLinerInput = this.$el.querySelector(
        'input[name="personalization-liner"]:checked'
      );
      if (checkedLinerInput) {
        this.selectedLiner = checkedLinerInput.value;
      }

      // --- Name defaults ---
      const defaultNameRaw = this.$el.dataset.nameDefault || '';
      if (this.isTwoLineInput) {
        const parts = defaultNameRaw.split(/\r?\n/);
        this.line1Text = parts[0] || '';
        this.line2Text = parts[1] || '';
        this.updateTwoLineName();
      } else {
        this.nameText = defaultNameRaw;
        const storedName = applyTextTransform(
          defaultNameRaw,
          this.$store.personalization.textTransform
        );
        this.$store.personalization.setName(storedName);
      }

      // --- Return address initial state (driven by data attribute set in Liquid) ---
      const initialChecked = this.$el.dataset.returnAddressInitial === 'true';
      this.showAddress = initialChecked;
      if (initialChecked) {
        this.$store.personalization.setReturnAddressEnabled(true);
      }

      if (this.$el.dataset.returnAddressDebug === 'true') {
        const debugPayload = {
          currentValue: this.$el.dataset.returnAddressCurrentValue || '',
          yesValue: this.$el.dataset.returnAddressYesValue || '',
          noValue: this.$el.dataset.returnAddressNoValue || '',
          allValues: this.$el.dataset.returnAddressAllValues || '',
          yesPriceCents: this.$el.dataset.returnAddressYesPrice || '',
          noPriceCents: this.$el.dataset.returnAddressNoPrice || '',
          deltaCents: this.$el.dataset.returnAddressDelta || '',
          deltaAbsCents: this.$el.dataset.returnAddressDeltaAbs || '',
          deltaLabel: this.$el.dataset.returnAddressDeltaLabel || '',
          checkboxLabelText: this.$el.querySelector('.personalization-option__checkbox-label')?.textContent?.trim() || ''
        };

        console.groupCollapsed('[Personalization Debug] Return Address Delta');
        console.table(debugPayload);
        console.groupEnd();
      }
    },

    /**
     * Called when the user selects a lettering colour.
     */
    updateColor(name, value) {
      this.selectedColor = { name, value };
      this.$store.personalization.setColor(name, value);
      this.$dispatch('personalization-changed', this.$store.personalization);
    },

    /**
     * Called when the user selects a font.
     */
    updateFont(name) {
      this.selectedFont = { name };
      this.$store.personalization.setFont(name);

      // Update text transform for the new font and re-apply to current name
      const transform = this.fontTransforms[name] || '';
      this.$store.personalization.setTextTransform(transform);

      if (this.isTwoLineInput) {
        const storedLine1 = applyTextTransform(this.line1Text, transform);
        const storedLine2 = applyTextTransform(this.line2Text, transform);
        this.$store.personalization.setNameLines(storedLine1, storedLine2);
        this.$store.personalization.setName(
          this.buildTwoLineStoredName(storedLine1, storedLine2)
        );
      } else {
        const stored = applyTextTransform(this.nameText, transform);
        this.$store.personalization.setName(stored);
      }

      this.$dispatch('personalization-changed', this.$store.personalization);
    },

    /**
     * Sync custom liner radios back to the hidden native liner option radios.
     */
    updateLiner(value) {
      this.selectedLiner = value;

      const linerFieldset = document.querySelector('[data-liner-option]');
      if (linerFieldset) {
        const targetInput = Array.from(
          linerFieldset.querySelectorAll('input[type="radio"][data-liner-option-value]')
        ).find((input) => input.dataset.linerOptionValue === value);

        if (targetInput && !targetInput.checked) {
          targetInput.click();
          targetInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this.$dispatch('personalization-changed', this.$store.personalization);
    },

    /**
     * Called on each keystroke in the name field.
     * nameText keeps the raw (untransformed) input value for cursor stability;
     * the store receives the text-transformed version.
     */
    updateName(text) {
      this.nameText = text;
      const transform = this.$store.personalization.textTransform;
      const stored = applyTextTransform(text, transform);
      this.$store.personalization.setName(stored);
      this.$dispatch('personalization-changed', this.$store.personalization);
    },

    updateLine1(text) {
      this.line1Text = text;
      this.updateTwoLineName();
    },

    updateLine2(text) {
      this.line2Text = text;
      this.updateTwoLineName();
    },

    /**
     * Toggle the return-address section.
     * Finds the native Stretch variant-picker "Return Address" fieldset
     * (hidden via CSS) and programmatically clicks the correct radio so the
     * variant selection updates (and triggers a product:rerender for price, etc.).
     */
    toggleReturnAddress(enabled) {
      this.showAddress = enabled;
      this.$store.personalization.setReturnAddressEnabled(enabled);

      const returnAddressFieldset = document.querySelector('[data-return-address-option]');
      if (returnAddressFieldset) {
        const yesValue = (this.$el.dataset.returnAddressYesValue || 'Yes').trim();
        const noValue = (this.$el.dataset.returnAddressNoValue || 'No').trim();
        const targetValue = enabled ? yesValue : noValue;
        const normalize = (value) => String(value || '').trim().toLowerCase();

        // The input values are option_value IDs (numeric), not the string "Yes"/"No".
        // Locate the label whose visible text matches "Yes" or "No", then click
        // the associated radio input via the label's `for` attribute.
        const allLabels = returnAddressFieldset.querySelectorAll('label.block-swatch');
        let targetInput = null;

        for (const label of allLabels) {
          const labelText = normalize(label.textContent);
          const normalizedTarget = normalize(targetValue);

          if (labelText === normalizedTarget || labelText.includes(normalizedTarget)) {
            const inputId = label.getAttribute('for');
            if (inputId) targetInput = document.getElementById(inputId);
            break;
          }
        }

        if (targetInput && !targetInput.checked) {
          targetInput.click();
          targetInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      if (!enabled) {
        this.returnAddressText = '';
        this.$store.personalization.setReturnAddressText('');
      }

      this.$dispatch('personalization-changed', this.$store.personalization);
    },

    /**
     * Called on each keystroke in the return address textarea.
     */
    updateReturnAddress(text) {
      this.returnAddressText = text;
      this.$store.personalization.setReturnAddressText(text);
      this.$dispatch('personalization-changed', this.$store.personalization);
    },

    isValid() {
      return this.$store.personalization.isValid();
    }
  }));

  // ============================================================
  // COMPONENT — personalizationRender
  // Renders the live preview image + text overlay.
  // One instance exists inline in the gallery; another inside the preview modal.
  // Both share the $store.personalization reactive state.
  // ============================================================

  Alpine.data('personalizationRender', () => ({
    positionX: 50,
    positionY: 50,
    fontSizeCss: '1rem',
    verticalOffset: 0,
    rotation: 0,
    defaultColorName: '',
    defaultColorValue: '',
    fontSizes: {},
    mobileFontSizes: {},
    fontOffsets: {},

    // Per-instance variant → image URL map (parsed from data-variant-images attr)
    variantImages: {},
    currentImage: '',

    _variantChangeHandler: null,

    init() {
      // --- Position from data attributes ---
      this.positionX = parseFloat(this.$el.dataset.positionX) || 50;
      this.positionY = parseFloat(this.$el.dataset.positionY) || 50;
      this.rotation  = parseFloat(this.$el.dataset.rotation)  || 0;
      this.defaultColorName = (this.$el.dataset.defaultColorName || '').trim();
      this.defaultColorValue = (this.$el.dataset.defaultColorValue || '').trim();

      if (!this.$store.personalization.color.value && this.defaultColorValue) {
        this.$store.personalization.setColor(this.defaultColorName || 'Default', this.defaultColorValue);
      }

      // --- Font size / offset maps ---
      try {
        this.fontSizes       = JSON.parse(this.$el.dataset.fontSizes       || '{}');
        this.mobileFontSizes = JSON.parse(this.$el.dataset.mobileFontSizes || '{}');
        this.fontOffsets     = JSON.parse(this.$el.dataset.fontOffsets     || '{}');
      } catch (e) {
        this.fontSizes = {};
        this.mobileFontSizes = {};
        this.fontOffsets = {};
      }

      // --- Variant image map ---
      try {
        this.variantImages = JSON.parse(this.$el.dataset.variantImages || '{}');
      } catch (e) {
        this.variantImages = {};
      }

      // --- Set initial preview image ---
      const initialId = this.$el.dataset.initialVariantId;
      if (initialId) {
        const url =
          this.variantImages[initialId] ||
          this.variantImages[String(initialId)];
        if (url) this.currentImage = url;
      }

      // --- Font size updater ---
      const updateFontSize = (fontName) => {
        if (!fontName) return;
        const mobilePx = this.mobileFontSizes[fontName];
        const desktopPx = this.fontSizes[fontName];
        this.fontSizeCss = buildFluidFontSize(mobilePx, desktopPx);
        this.verticalOffset = this.fontOffsets[fontName] || 0;
      };

      // React to font changes in the store
      this.$watch('$store.personalization.font.name', (fontName) => {
        updateFontSize(fontName);
      });

      // Apply initial font sizing
      const initialFont = this.$store.personalization.font.name;
      if (initialFont) updateFontSize(initialFont);

      // --- Variant change listener (swap preview image) ---
      // The Stretch theme's VariantPicker dispatches `variant:change` from the
      // form element with bubbles:true (theme.js ~line 3845), so we can catch
      // it at document level.
      this._variantChangeHandler = (e) => {
        const vid = String(e.detail?.variant?.id ?? '');
        if (vid && this.variantImages[vid]) {
          this.currentImage = this.variantImages[vid];
        }
      };
      document.addEventListener('variant:change', this._variantChangeHandler);
    },

    destroy() {
      if (this._variantChangeHandler) {
        document.removeEventListener('variant:change', this._variantChangeHandler);
      }
    }
  }));
}

document.addEventListener('alpine:init', registerPersonalizationComponents);

// Configurator pages can load scripts in different timing paths; this ensures
// registration still occurs if Alpine is already present when this file runs.
if (window.Alpine) {
  registerPersonalizationComponents();
}

// ============================================================
// FORM SUBMIT — inject personalization data as line item properties
// ============================================================

// ============================================================
// LIVE PROPERTY SYNC
// The theme's <product-form> custom element captures FormData synchronously
// inside its own submit handler, before any submit listener we attach can run.
// To guarantee properties are always included, we keep hidden inputs in the
// form at all times and update them whenever personalization state changes.
// ============================================================

function syncPersonalizationInputs(form, store) {
  if (!form || !store) return;

  const cartProperties = store.getCartProperties();
  const managedKeys = typeof store.getManagedPropertyKeys === 'function'
    ? store.getManagedPropertyKeys()
    : ['Lettering Color', 'Font', 'Name', 'Return Address'];

  managedKeys.forEach((key) => {
    const name = `properties[${key}]`;
    let input = form.querySelector(`input[name="${name}"]`);

    const rawValue = cartProperties[key];
    const value =
      typeof rawValue === 'string'
        ? (rawValue.trim() === '' ? '' : rawValue)
        : (rawValue || '');

    if (value) {
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value;
    } else {
      // Remove input if value is empty so empty properties aren't sent
      if (input) input.remove();
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const productForm = document.querySelector('form[action*="/cart/add"]');
  if (!productForm) return;

  // Wait for Alpine to be ready, then set up reactive sync
  document.addEventListener('alpine:init', () => {
    // Give Alpine a tick to register the store
    queueMicrotask(() => {
      const store = window.Alpine?.store?.('personalization');
      if (!store) return;

      // Initial sync in case values are already set
      syncPersonalizationInputs(productForm, store);

      // Re-sync on every personalization-changed event dispatched by Alpine components
      productForm.addEventListener('personalization-changed', () => {
        syncPersonalizationInputs(productForm, Alpine.store('personalization'));
      });

      // Also catch the event bubbled from anywhere in the document
      document.addEventListener('personalization-changed', () => {
        syncPersonalizationInputs(productForm, Alpine.store('personalization'));
      });
    });
  });

  // Fallback: also sync on submit in case event wiring was delayed
  productForm.addEventListener('submit', () => {
    syncPersonalizationInputs(productForm, window.Alpine?.store?.('personalization'));
  }, { capture: true });
});
